/**
 * useOptionChain.js
 *
 * TanStack Query hook that polls the option chain snapshot at 1-second
 * intervals and enriches it with OI change (delta vs previous snapshot).
 *
 * Returns:
 *   chain        — classified rows with oiChg, isAtm, ceItm, peItm
 *   spot         — underlying spot price
 *   expiry       — active expiry string
 *   tte          — time-to-expiry in years
 *   refreshedAt  — unix timestamp of last GreeksEngine write
 *   stale        — true if engine hasn't written in >5s
 *   pcr          — put-call ratio (total PE OI / total CE OI)
 *   isLoading    — first fetch in flight
 *   isError      — unrecoverable fetch error
 *   error        — error object
 */
import { useQuery } from '@tanstack/react-query';
import { useRef } from 'react';
import axiosInstance from '../../axiosConfig';
import { classifyChain, applyOiChange, computePCR } from './optionChainUtils';

const REFETCH_INTERVAL = 1_000; // 1 second — matches GreeksEngine refresh cycle

async function fetchSnapshot(underlying, expiry) {
    const params = expiry ? { expiry } : {};
    const { data } = await axiosInstance.get(
        `/api/option-chain/${encodeURIComponent(underlying)}`,
        { params }
    );
    return data;
}

export default function useOptionChain(underlying, expiry) {
    const prevChainRef = useRef(null);

    const query = useQuery({
        queryKey: ['optionChain', underlying, expiry],
        queryFn: () => fetchSnapshot(underlying, expiry),
        enabled: !!underlying,
        refetchInterval: REFETCH_INTERVAL,
        staleTime: 0,            // always re-fetch — data changes every second
        retry: 2,
        retryDelay: 500,
    });

    if (!query.data) {
        return {
            chain: [],
            spot: null,
            expiry: null,
            tte: null,
            refreshedAt: null,
            stale: true,
            pcr: null,
            ...query,
        };
    }

    const raw    = query.data;
    const rawChain = raw.chain ?? [];

    // Compute OI change against previous snapshot before updating the ref
    const withOiChg = applyOiChange(rawChain, prevChainRef.current);
    prevChainRef.current = rawChain;

    // Classify each row (ATM, CE-ITM, PE-ITM flags)
    const chain = classifyChain(withOiChg, raw.spot);
    const pcr   = computePCR(chain);

    return {
        chain,
        spot:        raw.spot,
        expiry:      raw.expiry,
        tte:         raw.tte_years,
        refreshedAt: raw.refreshed_at,
        stale:       raw.stale ?? false,
        pcr,
        isLoading:   query.isLoading,
        isError:     query.isError,
        error:       query.error,
    };
}

// ─── Expiry list hook ─────────────────────────────────────────────────────────

export function useExpiries(underlying) {
    return useQuery({
        queryKey: ['ocExpiries', underlying],
        queryFn: async () => {
            const { data } = await axiosInstance.get(
                `/api/option-chain/${encodeURIComponent(underlying)}/expiries`
            );
            return data.expiries ?? [];
        },
        enabled: !!underlying,
        staleTime: 60_000,   // expiries don't change mid-session
        retry: 1,
    });
}

// ─── Underlyings list hook ────────────────────────────────────────────────────

export function useUnderlyings() {
    return useQuery({
        queryKey: ['ocUnderlyings'],
        queryFn: async () => {
            const { data } = await axiosInstance.get('/api/option-chain/underlyings');
            return data.underlyings ?? [];
        },
        staleTime: 30_000,
        retry: 1,
    });
}

// ─── Subscription settings hooks ─────────────────────────────────────────────

export function useSubscriptionConfig(underlying) {
    return useQuery({
        queryKey: ['ocSubscription', underlying],
        queryFn: async () => {
            const { data } = await axiosInstance.get(
                `/api/option-chain/subscriptions/${encodeURIComponent(underlying)}`
            );
            return data;
        },
        enabled: !!underlying,
        staleTime: 5_000,
        retry: 1,
    });
}

export async function saveSubscriptionConfig(underlying, payload) {
    const { data } = await axiosInstance.post(
        `/api/option-chain/subscriptions/${encodeURIComponent(underlying)}`,
        payload
    );
    return data;
}

export async function resetSubscriptionConfig(underlying) {
    const { data } = await axiosInstance.post(
        `/api/option-chain/subscriptions/${encodeURIComponent(underlying)}/reset`
    );
    return data;
}
